import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipLink from "@/components/SkipLink";
import SectionHeader from "@/components/SectionHeader";
import CTAButton from "@/components/CTAButton";
import { StructuredData, SEO } from "@/components/SEO";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";

export default function ContactPage() {
  const breadcrumbData = {
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://fiteasy.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Contact",
        item: "https://fiteasy.com/contact",
      },
    ],
  };

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "support@fiteasy.com",
      href: "mailto:support@fiteasy.com",
    },
    {
      icon: Phone,
      label: "Phone / WhatsApp",
      value: "+1 (555) 123-4567",
      href: "tel:+15551234567",
    },
    {
      icon: MapPin,
      label: "Location",
      value: "123 Fitness Avenue, Health City, HC 12345",
      href: "https://maps.google.com/?q=Fitness+Avenue",
    },
  ];

  return (
    <>
      <SEO
        title="Contact"
        description="Contact the FitEasy team for support, partnerships, and trainer or nutritionist applications. We're here to help with your fitness journey."
        path="/contact"
      />
      <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      <div className="bg-gradient-to-b from-slate-50 to-blue-50 text-gray-900 min-h-screen">
        <SkipLink />
        <Header />

        <main id="main-content" className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
          <SectionHeader
            id="contact-heading"
            label="Contact"
            title="Let's talk about your fitness journey"
            description="Whether you're a member, trainer, nutritionist, or partner brand, we'd love to hear from you. Reach out using the contact details below or connect with us through the FitEasy app."
            className="mb-10"
          />

          <section aria-labelledby="contact-heading" className="max-w-3xl mx-auto">
            <div className="space-y-6">
              <article className="rounded-3xl border border-blue-100 bg-white/90 p-6">
                <h2 className="font-display text-lg text-gray-900 mb-4">
                  Direct contact
                </h2>
                <div className="space-y-4" role="list">
                  {contactInfo.map(({ icon: Icon, ...info }) => (
                    <a
                      key={info.label}
                      href={info.href}
                      className="flex items-start gap-3 rounded-2xl border border-blue-200 bg-white/80 p-4 transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                      aria-label={`${info.label}: ${info.value}`}
                      role="listitem"
                    >
                      <Icon className="mt-1 h-5 w-5 text-blue-600 flex-shrink-0" aria-hidden="true" />
                      <div>
                        <p className="text-xs uppercase tracking-[0.35em] text-blue-700 font-semibold">
                          {info.label}
                        </p>
                        <p className="mt-1 font-semibold text-gray-900">
                          {info.value}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </article>
              <article className="rounded-3xl border border-blue-100 bg-white/90 p-6">
                <h2 className="font-display text-lg text-gray-900 mb-4">
                  Trainers & nutritionists
                </h2>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  Interested in joining FitEasy as a professional? Apply directly
                  inside the app or reach out with your credentials and city.
                </p>
                <CTAButton
                  href="/programs"
                  variant="outline"
                  className="w-full justify-center text-sm"
                  ariaLabel="Apply to become a trainer or nutritionist"
                >
                  Learn More
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </CTAButton>
              </article>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

